import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  Row,
  Col,
  App,
  Alert,
  Popconfirm,
  Modal,
  Segmented,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSalesInvoices, useDeleteDraftSalesInvoice } from '../../hooks/useSalesInvoice';
import { SalesInvoice } from '../../services/salesInvoice.service';
import salesOrderTimesheetService from '../../services/salesOrderTimesheet.service';
import { CreateInvoiceFromDeliveriesModal, CreateAdvanceInvoiceModal, SelectSalesOrderForAdvanceModal } from '@/components/sales-invoice';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAiListSelection } from '@/hooks/useAiListSelection';

const { Title, Text } = Typography;
const { Option } = Select;

type RegisterView = 'invoices' | 'by_customer';

type CustomerInvoiceSummaryRow = {
  key: string;
  customer_id: string | null;
  customer_name: string;
  currency: string;
  invoice_count: number;
  open_count: number;
  cancelled_count: number;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
};

type DeliveryInvoiceLocationState = {
  openDeliveryInvoice?: boolean;
  customerId?: string;
  salesOrderId?: string;
};

type SalesInvoicesPageProps = {
  /** `proforma`: Finance register of advance (proforma) invoices only. */
  mode?: 'all' | 'proforma';
};

function invoiceStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Draft: t('statusDraft'),
    Approved: t('statusApproved'),
    Sent: t('statusSent'),
    Paid: t('statusPaid'),
    Cancelled: t('statusCancelled'),
    Submitted: t('statusSubmitted'),
  };
  return map[status] ?? status;
}

function paymentStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Unpaid: t('unpaid'),
    'Partially Paid': t('partiallyPaid'),
    Paid: t('paid'),
  };
  return map[status] ?? status;
}

const SalesInvoicesPage: React.FC<SalesInvoicesPageProps> = ({ mode = 'all' }) => {
  const { t } = useTranslation('growth');
  const { t: tc } = useTranslation('common');
  const isProforma = mode === 'proforma';
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const deleteDraftMutation = useDeleteDraftSalesInvoice();
  const [registerView, setRegisterView] = useState<RegisterView>('invoices');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [customerIdFilter, setCustomerIdFilter] = useState<string | null>(null);
  const [createFromDeliveriesOpen, setCreateFromDeliveriesOpen] = useState(false);
  const [deliveryInvoicePrefill, setDeliveryInvoicePrefill] = useState<{
    customerId?: string;
    salesOrderId?: string;
  } | null>(null);
  const [showAdvanceInvoiceSelect, setShowAdvanceInvoiceSelect] = useState(false);
  const [selectedSalesOrderIdForAdvance, setSelectedSalesOrderIdForAdvance] = useState<string | null>(null);
  const [createFromTimesheetOpen, setCreateFromTimesheetOpen] = useState(false);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<string | null>(null);

  const { data: approvedTimesheets = [], isLoading: approvedTsLoading } = useQuery({
    queryKey: ['so-timesheets-approved-for-invoice'],
    queryFn: () => salesOrderTimesheetService.listApprovedForInvoice(),
    enabled: createFromTimesheetOpen,
  });

  const createFromTimesheetMutation = useMutation({
    mutationFn: (id: string) => salesOrderTimesheetService.createInvoice(id),
    onSuccess: (inv: any) => {
      message.success(t('invoiceCreatedFromTimesheet'));
      setCreateFromTimesheetOpen(false);
      setSelectedTimesheetId(null);
      if (inv?.id) navigate(`/growth/invoices/${inv.id}`);
      else refetch();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || t('failedToCreateInvoice')),
  });

  const { data: invoicesData, isLoading, refetch, error } = useSalesInvoices({
    invoice_status: statusFilter || undefined,
    payment_status: paymentFilter || undefined,
    search: debouncedSearch || undefined,
    customer_id: customerIdFilter || undefined,
    invoice_type: isProforma ? 'Advance' : undefined,
  });

  const invoices: SalesInvoice[] = Array.isArray(invoicesData) ? invoicesData : (invoicesData as any)?.data ?? [];
  const { rowSelection, selectionCount } = useAiListSelection({
    entityType: 'SalesInvoice',
    module: 'sales',
    listFilters: {
      status: statusFilter || undefined,
      payment: paymentFilter || undefined,
      search: debouncedSearch || undefined,
      customer_id: customerIdFilter || undefined,
    },
    labelById: (id) => invoices.find((i) => i.id === id)?.invoice_number,
  });

  const customerSummaryRows = useMemo((): CustomerInvoiceSummaryRow[] => {
    const map = new Map<string, CustomerInvoiceSummaryRow>();
    for (const inv of invoices) {
      const customerId = String(inv.customer_id ?? '').trim() || null;
      const customerName = String(inv.customer_name ?? '').trim() || '—';
      const currency = String(inv.currency ?? '').trim() || '—';
      const key = `${customerId ?? customerName}::${currency}`;
      let row = map.get(key);
      if (!row) {
        row = {
          key,
          customer_id: customerId,
          customer_name: customerName,
          currency,
          invoice_count: 0,
          open_count: 0,
          cancelled_count: 0,
          grand_total: 0,
          paid_amount: 0,
          balance_due: 0,
        };
        map.set(key, row);
      }
      row.invoice_count += 1;
      if (inv.invoice_status === 'Cancelled') {
        row.cancelled_count += 1;
        continue;
      }
      row.open_count += 1;
      row.grand_total += Number(inv.grand_total ?? 0);
      row.paid_amount += Number(inv.paid_amount ?? 0);
      row.balance_due += Number(inv.balance_due ?? 0);
    }
    return [...map.values()].sort((a, b) => {
      const byName = a.customer_name.localeCompare(b.customer_name, undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      return a.currency.localeCompare(b.currency);
    });
  }, [invoices]);

  React.useEffect(() => {
    if (error) {
      const errMsg = (error as any)?.response?.data?.message ?? (error as any)?.message ?? t('failedToLoadInvoices');
      message.error(errMsg);
    }
  }, [error, message, t]);

  useEffect(() => {
    const s = location.state as DeliveryInvoiceLocationState | null | undefined;
    if (s?.openDeliveryInvoice && s.customerId) {
      setCreateFromDeliveriesOpen(true);
      setDeliveryInvoicePrefill({ customerId: s.customerId, salesOrderId: s.salesOrderId });
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  const columns: ColumnsType<SalesInvoice> = [
    {
      title: t('invoiceNo'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      fixed: 'left',
      width: 130,
      render: (val: string, row: SalesInvoice) => (
        <a onClick={() => navigate(`/growth/invoices/${row.id}`)}>{val ?? '-'}</a>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: 110,
      render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: t('customer'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: t('soNumber'),
      key: 'so_number',
      width: 120,
      render: (_: unknown, row: SalesInvoice) =>
        (row.salesOrder as any)?.sales_order_number ?? (row as any).sales_order_number ?? '-',
    },
    {
      title: t('deliveryNotes'),
      key: 'delivery_notes',
      width: 160,
      ellipsis: true,
      render: (_: unknown, row: SalesInvoice) => {
        const labels =
          row.delivery_note_numbers?.length
            ? row.delivery_note_numbers
            : row.delivery_note_number
              ? [row.delivery_note_number]
              : [];
        return labels.length ? labels.join(', ') : '—';
      },
    },
    {
      title: t('type'),
      dataIndex: 'invoice_type',
      key: 'invoice_type',
      width: 100,
      render: (v: string) =>
        v === 'Advance' ? <Tag color="purple">{t('proforma')}</Tag> : <Tag>{v ? (v === 'Standard' ? t('standard') : v) : t('standard')}</Tag>,
    },
    {
      title: t('grandTotal'),
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      width: 120,
      render: (v: number, row: SalesInvoice) =>
        v != null ? formatCurrency(Number(v), row.currency) : '-',
    },
    {
      title: t('status'),
      dataIndex: 'invoice_status',
      key: 'invoice_status',
      width: 110,
      render: (v: string) => (
        <Tag color={v === 'Draft' ? 'default' : v === 'Sent' || v === 'Approved' ? 'blue' : v === 'Paid' ? 'green' : 'orange'}>
          {v ? invoiceStatusLabel(v, t) : '-'}
        </Tag>
      ),
    },
    {
      title: t('filterPayment'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 110,
      render: (v: string) => (
        <Tag color={v === 'Paid' ? 'green' : v === 'Partially Paid' ? 'orange' : 'default'}>
          {paymentStatusLabel(v ?? 'Unpaid', t)}
        </Tag>
      ),
    },
    {
      title: t('glPosted'),
      dataIndex: 'gl_posted',
      key: 'gl_posted',
      width: 90,
      align: 'center',
      render: (v: boolean) => (v ? <Tag color="green">{tc('yes')}</Tag> : <Tag>{tc('no')}</Tag>),
    },
    {
      title: tc('actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: unknown, row: SalesInvoice) => {
        const isDraft = row.invoice_status === 'Draft';
        if (!isDraft) return null;
        return (
          <Popconfirm
            title={t('deleteDraftTitle')}
            description={t('deleteDraftDescription')}
            okText={tc('delete')}
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteDraftMutation.mutateAsync(row.id)}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteDraftMutation.isPending}
            >
              {tc('delete')}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const customerSummaryColumns: ColumnsType<CustomerInvoiceSummaryRow> = [
    {
      title: t('customer'),
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
      render: (name: string, row) => (
        <a
          onClick={() => {
            setCustomerIdFilter(row.customer_id);
            if (!row.customer_id) setSearchText(row.customer_name === '—' ? '' : row.customer_name);
            setRegisterView('invoices');
          }}
        >
          {name}
        </a>
      ),
    },
    {
      title: t('currency'),
      dataIndex: 'currency',
      key: 'currency',
      width: 90,
    },
    {
      title: t('invoiceCount'),
      dataIndex: 'invoice_count',
      key: 'invoice_count',
      width: 100,
      align: 'right',
    },
    {
      title: t('openInvoices'),
      dataIndex: 'open_count',
      key: 'open_count',
      width: 90,
      align: 'right',
    },
    {
      title: t('cancelledInvoices'),
      dataIndex: 'cancelled_count',
      key: 'cancelled_count',
      width: 100,
      align: 'right',
      render: (v: number) => (v > 0 ? <Text type="secondary">{v}</Text> : '0'),
    },
    {
      title: t('grandTotal'),
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 130,
      align: 'right',
      render: (v: number, row) => formatCurrency(v, row.currency !== '—' ? row.currency : undefined),
    },
    {
      title: t('paidAmount'),
      dataIndex: 'paid_amount',
      key: 'paid_amount',
      width: 130,
      align: 'right',
      render: (v: number, row) => formatCurrency(v, row.currency !== '—' ? row.currency : undefined),
    },
    {
      title: t('balanceDue'),
      dataIndex: 'balance_due',
      key: 'balance_due',
      width: 130,
      align: 'right',
      render: (v: number, row) => (
        <Text type={v > 0 ? 'danger' : undefined}>
          {formatCurrency(v, row.currency !== '—' ? row.currency : undefined)}
        </Text>
      ),
    },
    {
      title: tc('actions'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setCustomerIdFilter(row.customer_id);
            if (!row.customer_id) setSearchText(row.customer_name === '—' ? '' : row.customer_name);
            setRegisterView('invoices');
          }}
        >
          {t('viewCustomerInvoices')}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {isProforma ? t('proformaInvoices') : t('taxInvoices')}
          </Title>
          <Text type="secondary">
            {isProforma ? t('proformaInvoicesSubtitle') : t('taxInvoicesManageSubtitle')}
          </Text>
          {selectionCount > 0 ? (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {t('selectedForAi', { count: selectionCount })}
            </Tag>
          ) : null}
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message={isProforma ? t('proformaRegisterMessage') : t('taxRegisterMessage')}
            description={
              isProforma ? t('proformaRegisterDescription') : t('taxRegisterDescription')
            }
          />
        </Col>
        <Col>
          <Space>
            {!isProforma ? (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateFromDeliveriesOpen(true)}
                >
                  {t('createInvoiceFromDeliveries')}
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => setCreateFromTimesheetOpen(true)}>
                  {t('createInvoiceFromTimesheet')}
                </Button>
              </>
            ) : null}
            <Button
              type={isProforma ? 'primary' : 'default'}
              icon={<PlusOutlined />}
              onClick={() => setShowAdvanceInvoiceSelect(true)}
            >
              {t('createProformaInvoice')}
            </Button>
          </Space>
        </Col>
      </Row>

      <CreateInvoiceFromDeliveriesModal
        open={createFromDeliveriesOpen}
        initialCustomerId={deliveryInvoicePrefill?.customerId}
        initialSalesOrderId={deliveryInvoicePrefill?.salesOrderId}
        onClose={() => {
          setCreateFromDeliveriesOpen(false);
          setDeliveryInvoicePrefill(null);
        }}
        onSuccess={(invoiceId) => {
          setDeliveryInvoicePrefill(null);
          navigate(`/growth/invoices/${invoiceId}`);
        }}
      />

      <Modal
        title={t('createInvoiceFromTimesheet')}
        open={createFromTimesheetOpen}
        onCancel={() => {
          setCreateFromTimesheetOpen(false);
          setSelectedTimesheetId(null);
        }}
        onOk={() => {
          if (!selectedTimesheetId) {
            message.warning(t('selectApprovedTimesheetWarning'));
            return;
          }
          return createFromTimesheetMutation.mutateAsync(selectedTimesheetId);
        }}
        confirmLoading={createFromTimesheetMutation.isPending}
        okText={t('createInvoice')}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('timesheetInvoiceHint')}
        </Text>
        <Select
          style={{ width: '100%' }}
          loading={approvedTsLoading}
          placeholder={t('selectApprovedTimesheet')}
          value={selectedTimesheetId || undefined}
          onChange={(v) => setSelectedTimesheetId(v)}
          options={approvedTimesheets.map((ts) => ({
            value: ts.id,
            label: `${ts.sales_order_number} · ${ts.period_year}-${String(ts.period_month).padStart(2, '0')} · ${ts.customer_name || ''}`,
          }))}
        />
      </Modal>

      <SelectSalesOrderForAdvanceModal
        open={showAdvanceInvoiceSelect}
        onClose={() => setShowAdvanceInvoiceSelect(false)}
        onSelectSalesOrder={(id) => {
          setShowAdvanceInvoiceSelect(false);
          setSelectedSalesOrderIdForAdvance(id);
        }}
      />

      <CreateAdvanceInvoiceModal
        open={!!selectedSalesOrderIdForAdvance}
        onClose={() => setSelectedSalesOrderIdForAdvance(null)}
        onSuccess={(invoiceId) => {
          setSelectedSalesOrderIdForAdvance(null);
          navigate(`/growth/invoices/${invoiceId}`);
        }}
        salesOrderId={selectedSalesOrderIdForAdvance ?? ''}
      />

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Segmented<RegisterView>
            value={registerView}
            onChange={(v) => {
              setRegisterView(v);
              // Customer summary should aggregate the full filtered register, not a single drill-down.
              if (v === 'by_customer') setCustomerIdFilter(null);
            }}
            options={[
              { value: 'invoices', label: t('registerViewInvoices') },
              { value: 'by_customer', label: t('registerViewByCustomer') },
            ]}
          />
          <Input
            placeholder={t('searchSalesInvoices')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            placeholder={t('filterStatus')}
            value={statusFilter || undefined}
            onChange={setStatusFilter}
            style={{ width: 140 }}
            allowClear
          >
            <Option value="Draft">{invoiceStatusLabel('Draft', t)}</Option>
            <Option value="Approved">{invoiceStatusLabel('Approved', t)}</Option>
            <Option value="Sent">{invoiceStatusLabel('Sent', t)}</Option>
            <Option value="Paid">{invoiceStatusLabel('Paid', t)}</Option>
            <Option value="Cancelled">{invoiceStatusLabel('Cancelled', t)}</Option>
          </Select>
          <Select
            placeholder={t('filterPayment')}
            value={paymentFilter || undefined}
            onChange={setPaymentFilter}
            style={{ width: 140 }}
            allowClear
          >
            <Option value="Unpaid">{paymentStatusLabel('Unpaid', t)}</Option>
            <Option value="Partially Paid">{paymentStatusLabel('Partially Paid', t)}</Option>
            <Option value="Paid">{paymentStatusLabel('Paid', t)}</Option>
          </Select>
          {customerIdFilter ? (
            <Tag
              closable
              onClose={() => setCustomerIdFilter(null)}
              color="blue"
            >
              {t('customer')}:{' '}
              {customerSummaryRows.find((r) => r.customer_id === customerIdFilter)?.customer_name ||
                invoices.find((i) => i.customer_id === customerIdFilter)?.customer_name ||
                customerIdFilter}
            </Tag>
          ) : null}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            {tc('refresh')}
          </Button>
        </Space>

        {registerView === 'by_customer' ? (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {t('customerSummaryHint')}
            </Text>
            <Table<CustomerInvoiceSummaryRow>
              rowKey="key"
              columns={customerSummaryColumns}
              dataSource={customerSummaryRows}
              loading={isLoading}
              scroll={{ x: 1100, y: 520 }}
              sticky={{ offsetHeader: 0 }}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => t('totalCustomers', { total }),
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              size="small"
              summary={() => {
                const totalsByCurrency = new Map<string, { grand: number; paid: number; balance: number }>();
                for (const row of customerSummaryRows) {
                  const cur = row.currency || '—';
                  const acc = totalsByCurrency.get(cur) || { grand: 0, paid: 0, balance: 0 };
                  acc.grand += row.grand_total;
                  acc.paid += row.paid_amount;
                  acc.balance += row.balance_due;
                  totalsByCurrency.set(cur, acc);
                }
                const currencyEntries = [...totalsByCurrency.entries()];
                if (!currencyEntries.length) return null;
                return (
                  <Table.Summary fixed>
                    {currencyEntries.map(([currency, totals], idx) => (
                      <Table.Summary.Row key={currency}>
                        <Table.Summary.Cell index={0}>
                          {idx === 0 ? <Text strong>{t('total')}</Text> : null}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <Text type="secondary">{currency}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong>
                            {formatCurrency(totals.grand, currency !== '—' ? currency : undefined)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong>
                            {formatCurrency(totals.paid, currency !== '—' ? currency : undefined)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <Text strong type={totals.balance > 0 ? 'danger' : undefined}>
                            {formatCurrency(totals.balance, currency !== '—' ? currency : undefined)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} />
                      </Table.Summary.Row>
                    ))}
                  </Table.Summary>
                );
              }}
            />
          </>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={invoices}
            loading={isLoading}
            rowSelection={rowSelection}
            scroll={{ x: 1280, y: 520 }}
            sticky={{ offsetHeader: 0 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) =>
                isProforma
                  ? t('totalProformaInvoices', { total })
                  : t('totalInvoices', { total }),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            size="small"
          />
        )}
      </Card>
    </div>
  );
};

export default SalesInvoicesPage;
